import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from '../../components/SnackbarProvider';
import axiosInstance from '../../lib/axios';
import {
  CashMovement,
  CashMovementType,
  CashMovementReason,
  CreateCashMovementPayload,
} from '../../types/cashDrawer';

interface CashMovementModalProps {
  open: boolean;
  onClose: () => void;
  shiftId: string | null;
}

interface FormData {
  type: CashMovementType;
  amount: number;
  reason: CashMovementReason;
  note: string;
}

const defaultValues: FormData = {
  type: CashMovementType.CASH_OUT,
  amount: 0,
  reason: CashMovementReason.PETTY_EXPENSE,
  note: '',
};

export const CashMovementModal = ({ open, onClose, shiftId }: CashMovementModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ defaultValues });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const recordMovementMutation = useMutation({
    mutationFn: async (data: CreateCashMovementPayload): Promise<CashMovement> => {
      const response = await axiosInstance.post(
        `/cash-drawer/shifts/${shiftId}/movements`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-drawer-shift', shiftId] });
      queryClient.invalidateQueries({ queryKey: ['cash-drawer-current-shift'] });
      showMessage(t('cashDrawer.movementModal.success'), 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('cashDrawer.movementModal.error');
      showMessage(message, 'error');
    },
  });

  const onSubmit = async (data: FormData) => {
    if (!shiftId) return;
    await recordMovementMutation.mutateAsync({
      type: data.type,
      amount: Number(data.amount),
      reason: data.reason,
      note: data.note || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('cashDrawer.movementModal.title')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>{t('cashDrawer.movementModal.type')}</InputLabel>
                    <Select {...field} label={t('cashDrawer.movementModal.type')} disabled={isSubmitting}>
                      <MenuItem value={CashMovementType.CASH_IN}>
                        {t('cashDrawer.movement.cashIn')}
                      </MenuItem>
                      <MenuItem value={CashMovementType.CASH_OUT}>
                        {t('cashDrawer.movement.cashOut')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="amount"
                control={control}
                rules={{ required: true, min: 0.01 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label={t('cashDrawer.movementModal.amount')}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: '0.01' }}
                    error={!!errors.amount}
                    helperText={errors.amount ? t('cashDrawer.movementModal.amountRequired') : ''}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>{t('cashDrawer.movementModal.reason')}</InputLabel>
                    <Select {...field} label={t('cashDrawer.movementModal.reason')} disabled={isSubmitting}>
                      <MenuItem value={CashMovementReason.CHANGE_FUND}>
                        {t('cashDrawer.movementReason.changeFund')}
                      </MenuItem>
                      <MenuItem value={CashMovementReason.PETTY_EXPENSE}>
                        {t('cashDrawer.movementReason.pettyExpense')}
                      </MenuItem>
                      <MenuItem value={CashMovementReason.BANK_DEPOSIT}>
                        {t('cashDrawer.movementReason.bankDeposit')}
                      </MenuItem>
                      <MenuItem value={CashMovementReason.CORRECTION}>
                        {t('cashDrawer.movementReason.correction')}
                      </MenuItem>
                      <MenuItem value={CashMovementReason.OTHER}>
                        {t('cashDrawer.movementReason.other')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('cashDrawer.movementModal.note')}
                    fullWidth
                    multiline
                    rows={2}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t('cashDrawer.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !shiftId}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {t('cashDrawer.movementModal.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CashMovementModal;
